import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { THEME_COLOR } from "../../config/club"; // Ensure we use THEME_VIOLET
import MatchHistoryModal from "./MatchHistoryModal";

const SessionList = ({ title, sessions, clubId, resolvedUid, isPast = false, isOwnProfile, statsMap }) => {
    const navigate = useNavigate();
    const [selectedSession, setSelectedSession] = useState(null);
    const [isAutoRedirecting, setIsAutoRedirecting] = useState(false); // Local loading state

    const THEME_VIOLET = THEME_COLOR

    // 🔥 AUTOMATIC REDIRECT LOGIC
    useEffect(() => {
        // Only auto-redirect if it's an ACTIVE session list and we have exactly 1 session
        if (!isPast && sessions && sessions.length === 1) {
            const session = sessions[0];
            setIsAutoRedirecting(true);

            // Small delay to let the user see the "Joining" screen for better UX
            const timer = setTimeout(() => {
                navigate(`/join?club=${clubId}&session=${session.id}`);
            }, 1000);

            return () => clearTimeout(timer);
        }
    }, [sessions, isPast, clubId, navigate]);

    if (isAutoRedirecting) {
        return (
            <div className="position-fixed top-0 start-0 w-100 h-100 session-overlay d-flex flex-column align-items-center justify-content-center" style={{ zIndex: 9999 }}>
                <div className="spinner-border mb-3" style={{ color: THEME_VIOLET, width: '3rem', height: '3rem' }} />
                <div className="fw-bold" >Opening active session...</div>
            </div>
        );
    }

    if (!sessions || sessions.length === 0) return null;
    const sortedSessions = [...sessions].sort((a, b) => {
        const aSec = a.endedAt?.seconds ?? 0;
        const bSec = b.endedAt?.seconds ?? 0;
        return bSec - aSec; // DESC
    });

    //console.log(sessions)
    return (
        <div className="my-4 px-1">
            {/* Unified Title Style */}
            <div className="d-flex align-items-center gap-2 mb-3">
                {!isPast && (
                    <span className="spinner-grow spinner-grow-sm text-success" role="status" style={{ width: '8px', height: '8px' }}></span>
                )}
                <h6 className="fw-bold text-uppercase mb-0"
                    style={{
                        color: isPast 
  ? 'var(--bs-secondary-color)' 
  : THEME_VIOLET,
                        fontSize: '0.75rem',
                        letterSpacing: '1.2px'
                    }}>
                    {title}
                </h6>
            </div>

            <div className="d-flex flex-column gap-3 px-2">
                {sortedSessions.map((session) => {
                    const stats = statsMap?.[session.id];
                    const hasPlayed = stats && (stats.w + stats.l > 0);
                    const rank = stats?.rank;
                    const winRate = hasPlayed ? Math.round((stats.w / (stats.w + stats.l)) * 100) : 0;

                    const isTop3 = rank && rank <= 3;
                    const rankColor = rank === 1 ? '#FFD700' : rank === 2 ? '#94A3B8' : rank === 3 ? '#B45309' : '#64748B';

                    return (
                        <div
                            key={session.id}
                            onClick={() => {
                                if (isPast) {
                                    const sessionStats = statsMap?.[session.id];
                                    setSelectedSession({
                                        ...session,
                                        // Pass these directly into the session object or as separate props
                                        preLoadedStats: { wins: sessionStats?.w || 0, losses: sessionStats?.l || 0 },
                                        preLoadedRank: sessionStats?.rank
                                    });
                                } else {
                                    navigate(`/join?club=${clubId}&session=${session.id}`);
                                }
                            }}
                            className="card border-0 shadow-sm overflow-hidden session-card"
                            style={{
                                borderRadius: '24px',
                                border: isPast
  ? '1px solid var(--border-color)'
  : `1px solid ${THEME_VIOLET}40`,
backgroundColor: 'var(--card-bg)'
                            }}
                        >
                            <div className="card-body p-3 position-relative">
                                {/* Share Hint Icon Overlay */}
                                {hasPlayed && isPast && (
                                    <div className="position-absolute" style={{ top: '15px', right: '10px' }}>
                                        <div className="rounded-circle d-flex align-items-center justify-content-center"
                                            style={{ width: '24px', height: '24px', backgroundColor: `${THEME_VIOLET}10` }}>
                                            <i className="bi bi-share-fill" style={{ fontSize: '0.6rem', color: THEME_VIOLET }}></i>
                                        </div>
                                    </div>
                                )}

                                {/* 1. FULL WIDTH TITLE SECTION */}
                                <div className="mb-3">
                                    <div className="d-flex justify-content-between align-items-start mb-1">
                                        <span className={`badge rounded-pill mb-2 session-badge ${isPast ? "past" : "live"}`} style={{
                                            fontSize: '0.65rem',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px'
                                        }}>
                                            {session.endedAt?.toDate
                                                ? new Date(session.endedAt.toDate()).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                                                : 'Live Session'}
                                        </span>
                                        
                                    </div>
                                                
                                    <h5 className="fw-bold mb-0" style={{
                                        fontSize: '1.15rem',
                                        lineHeight: '1.4',
                                        letterSpacing: '-0.02em',
                                        wordBreak: 'break-word'
                                    }}>
                                        {session.name || `No Session Name`}
                                    </h5>
                                    <div className="position-absolute" style={{ top: '40%', right: '10px' }}>
                                        <div className="rounded-circle d-flex align-items-center justify-content-center"
                                            style={{ width: '24px', height: '24px' }}>
                                            <i
  className="bi bi-chevron-right"
  style={{ color: 'var(--bs-secondary-color)', opacity: 0.4 }}
></i>
                                        </div>
                                    </div>
                                    
                                </div>
                                    
                                {/* 2. DEDICATED DATA ROW */}
                                {hasPlayed ? (
                                    <div
                                        className="session-stats-container"
                                        style={{
                                            borderRadius: '20px',
                                        }}
                                    >
                                        {/* RANK BOX */}
                                        <div className="d-flex align-items-center gap-2">
                                            <div
                                                className="d-flex align-items-center justify-content-center shadow-sm"
                                                style={{
                                                    width: '38px', height: '38px',
                                                    borderRadius: '12px',
                                                    backgroundColor: isTop3 ? rankColor : THEME_COLOR,
                                                    color: isTop3 && rank === 1 ? '#000' : '#fff',
                                                }}
                                            >
                                                <span style={{ fontSize: '1rem', fontWeight: '900' }}>{rank || '-'}</span>
                                            </div>
                                            <div>
                                                <div className="rank-label" style={{ fontSize: '0.6rem', fontWeight: '800', textTransform: 'uppercase' }}>Rank</div>
                                                <div className="rank-sub" style={{ fontSize: '0.75rem', fontWeight: '700' }}>
                                                    {rank === 1 ? 'Top Seed' : rank <= 3 ? 'Elite Tier' : rank <= 10 ? 'High Flyer' : 'Contender'}
                                                </div>
                                            </div>
                                        </div>

                                        {/* STATS DIVIDER */}
                                        <div  className="session-divider" style={{ width: '1px', height: '24px'}}></div>

                                        {/* W-L-T */}
                                        <div className="text-center">
                                            <div className="rank-label" style={{ fontSize: '0.6rem', fontWeight: '800',  textTransform: 'uppercase' }}>Record</div>
                                            <div style={{ fontSize: '0.85rem', fontWeight: '700' }}>
                                                <span className="text-success">{stats.w}W</span>
                                                <span className="mx-1 text-muted opacity-50">•</span>
                                                <span className="text-danger">{stats.l}L</span>
                                            </div>
                                        </div>

                                        {/* WIN RATE */}
                                        <div className="text-end">
                                            <div className="rank-label" style={{ fontSize: '0.6rem', fontWeight: '800', textTransform: 'uppercase' }}>Win%</div>
                                            <div style={{
                                                fontSize: '0.95rem',
                                                fontWeight: '900',
                                                color: winRate >= 50 
  ? '#10B981' 
  : 'var(--text-color)'
                                            }}>
                                                {winRate}%
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    isPast && (
                                        <div className="no-match-pill" >
                                            <span style={{ color: 'var(--bs-secondary-color)' }}>No matches played in this session</span>
                                        </div>
                                    )
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Modal for Past Sessions */}
            {selectedSession && (
                <MatchHistoryModal
                    session={selectedSession}
                    passedStats={selectedSession.preLoadedStats}
                    passedRank={selectedSession.preLoadedRank}
                    clubId={clubId}
                    uid={resolvedUid}
                    onClose={() => setSelectedSession(null)}
                    isOwnProfile={isOwnProfile}
                />
            )}
        </div>
    );
};

export default SessionList;